import {
  Component,
  ElementRef,
  OnInit,
  ViewChild,
  ViewEncapsulation,
  AfterViewChecked,
  ChangeDetectorRef, ChangeDetectionStrategy
} from '@angular/core';
import { Location, NgIf, NgFor, NgClass } from '@angular/common';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { Meta, Title } from '@angular/platform-browser';

import {
  IDocument,
  IStrapiDocument,
  IStrapiDocuments,
  StudentWellbeing,
  StudentWellbeingArticle,
  Wellbeing,
} from 'src/app/interfaces/strapi';

import { ResidenceService } from 'src/app/services/residence.service';
import { PageComponent } from 'src/app/shared/components/page/page.component';
import StrapiService from 'src/app/services/strapi.service';
import { AuthService } from '../../../services/auth.service';
import { TabData, GenericTabsComponent } from '../generic-tabs/generic-tabs.component';
import { BreadcrumbsComponent } from '../../../shared/components/breadcrumbs/breadcrumbs.component';
import { PageRetrievalErrorComponent } from '../../../shared/components/page-retrieval-error/page-retrieval-error.component';
import { WellbeingHelplineComponent } from '../wellbeing-helpline/wellbeing-helpline.component';
import { RenderMarkdownComponent } from '../../../shared/components/render-markdown/render-markdown.component';
import { SignpostingCardComponent } from '../signposting-card/signposting-card.component';

@Component({
    selector: 'app-wellbeing',
    templateUrl: './wellbeing.component.html',
    styleUrls: ['./wellbeing.component.scss'],
    encapsulation: ViewEncapsulation.Emulated,
  changeDetection:ChangeDetectionStrategy.OnPush,
    standalone: true,
    imports: [
        BreadcrumbsComponent,
        NgIf,
        PageRetrievalErrorComponent,
        GenericTabsComponent,
        WellbeingHelplineComponent,
        NgFor,
        RenderMarkdownComponent,
        NgClass,
        SignpostingCardComponent,
    ],
})
export class WellbeingComponent
  extends PageComponent
  implements OnInit, AfterViewChecked
{
  public wellbeingHelplineData: Wellbeing = <Wellbeing>{};

  public wellbeingSections: StudentWellbeing[] = [];

  public wellbeingSectionInformation: StudentWellbeing = <StudentWellbeing>{};

  public wellbeingArticles: StudentWellbeingArticle[] = [];

  public wellbeingArticlesToShow: StudentWellbeingArticle[] = [];

  public initialTabToShow: string = '';

  public tabs: TabData[] = [];

  public selectedTabId: string = '';

  public isLoading: boolean = false;

  public gridClass = '';

  private setLoadingState(state: boolean): void {
    this.isLoading = state;
    this.cdr.detectChanges();
  }

  @ViewChild('wellbeingHelpline', { static: false })
  wellbeingHelpline?: ElementRef;

  @ViewChild('coreServicesContainer', { static: false })
  coreServicesContainer?: ElementRef;

  @ViewChild('helplineImage', { static: false }) helplineImage?: ElementRef;

  @ViewChild('wellbeingComponent', { static: false })
  wellbeingComponent?: ElementRef;

  @ViewChild('wellbeingTabsImg', { static: false })
  wellbeingTabsImg?: ElementRef;

  @ViewChild('infoCards', { static: false }) infoCards?: ElementRef;

  constructor(
    protected override readonly titleService: Title,
    protected override readonly metaService: Meta,

    public override readonly strapi: StrapiService,
    public readonly auth: AuthService,
    public readonly residenceService: ResidenceService,
    private readonly router: Router,
    private readonly activeRoute: ActivatedRoute,
    private readonly location: Location,
    private cdr: ChangeDetectorRef
  ) {
    super(titleService, metaService, strapi);
  }

  ngOnInit(): void {
    this.setLoadingState(true);
    this.activeRoute.queryParams.subscribe((params: Params) => {
      const { tab } = params;
      this.initialTabToShow = <string>tab;
      this.getWellbeingPageInfo();
    });

    this.updateGridClass();
  }

  ngAfterViewChecked() {
    if (
      this.wellbeingHelpline &&
      this.coreServicesContainer &&
      this.helplineImage
    ) {
      const helplineHeight = Number(
        this.wellbeingHelpline.nativeElement.offsetHeight
      );
      const servicesHeight = Number(
        this.coreServicesContainer.nativeElement.offsetHeight
      );
      const wellbeingHeight = Number(
        this.wellbeingComponent?.nativeElement.offsetHeight
      );

      const shortestHeight = Math.min(helplineHeight, servicesHeight);

      this.wellbeingHelpline.nativeElement.style.height = `${shortestHeight}px`;
      this.coreServicesContainer.nativeElement.style.height = `${shortestHeight}px`;

      const imgHeight = helplineHeight - wellbeingHeight;

      this.helplineImage.nativeElement.style.height = `${imgHeight}px`;
    }

    if (this.infoCards && this.wellbeingTabsImg) {
      const infoCardsHeight = Number(this.infoCards.nativeElement.offsetHeight);

      this.wellbeingTabsImg.nativeElement.style.height = `${infoCardsHeight}px`;
    }
  }

  public getWellbeingPageInfo(): void {
    this.strapi
      .getDocumentByUID('wellbeing', 'wellbeing', {
        populate: '*',
      })
      .subscribe((data: IStrapiDocument<Wellbeing>) => {
        this.wellbeingHelplineData = data.data.attributes;
        this.wellbeingHelplineData.uid = data.data.attributes.uid ?? '';
        this.pageData.browser_title = data.data.attributes.browser_title;
        this.pageData.meta_description = data.data.attributes.meta_description;

        this.setMetas();
      });

    this.strapi
      .getDocumentsByType('student-wellbeings', { populate: '*' })
      .subscribe({
        next: (data: IStrapiDocuments<StudentWellbeing>) => {
          const sorted = [...data.data].sort(
            (a: IDocument<StudentWellbeing>, b: IDocument<StudentWellbeing>) => {
              const wellbeingDataA = a.attributes;
              const wellbeingDataB = b.attributes;

              return wellbeingDataA.order_number - wellbeingDataB.order_number;
            }
          );

          this.tabs = sorted.map(
            (d: IDocument<StudentWellbeing>, index: number) => {
              const wellbeingData = d.attributes;
              wellbeingData.uid = d.attributes.uid ?? '';

              this.wellbeingSections.push(wellbeingData);

              let isActive = false;

              // Show tab from query params, otherwise default to 1st tab
              if (
                this.initialTabToShow &&
                this.initialTabToShow === wellbeingData.uid
              ) {
                isActive = true;
              } else if (!this.initialTabToShow && index === 0) {
                isActive = true;
                this.initialTabToShow = d.attributes.uid ?? '';
              }

              if (isActive) {
                this.selectedTabId = wellbeingData.uid;
              }

              return {
                id: wellbeingData.uid,
                title: wellbeingData.tab_name,
                isActive,
              };
            }
          );

          this.strapi
            .getDocumentsByType('wellbeing-articles', { populate: '*' })
            .subscribe({
              next: (res: IStrapiDocuments<StudentWellbeingArticle>) => {
                this.wellbeingArticles = res.data.map(
                  (d: IDocument<StudentWellbeingArticle>) => {
                    const article = d.attributes;
                    article.category_uid = article.category.data.attributes.uid;
                    article.uid = d.attributes.uid ?? '';
                    return article;
                  }
                );

                if (this.initialTabToShow) {
                  this.onTabChanged(this.initialTabToShow);
                }
              },
              error: () => this.setLoadingState(false),
              complete: () => this.setLoadingState(false),
            });
        },
        error: () => this.setLoadingState(false),
      });
  }

  public onTabChanged(tabUid: string): void {
    this.selectedTabId = tabUid;

    [this.wellbeingSectionInformation] = this.wellbeingSections.filter(
      (section) => section.uid === tabUid
    );

    this.wellbeingArticlesToShow = this.wellbeingArticles.filter(
      (article) => article.category_uid === tabUid
    );

    this.wellbeingArticlesToShow.sort((a, b) => {
      const dateA = new Date(a.date_published);
      const dateB = new Date(b.date_published);
      return dateB.getTime() - dateA.getTime();
    });

    this.location.replaceState(`app/wellbeing?tab=${tabUid}`);
    this.updateGridClass();
  }

  public onArticlePressed(article: StudentWellbeingArticle): void {
    this.router.navigate([`app/wellbeing/${article.uid}`]).finally(() => false);
  }

  private updateGridClass(): void {
    const itemCount = this.wellbeingArticlesToShow.length;

    if (itemCount % 3 === 0) {
      this.gridClass = 'three-cols';
    } else {
      this.gridClass = 'two-cols';
    }
  }
}
